import { useCallback, useEffect, useRef, useState } from 'react'
import { Package, Upload, Trash2, AlertTriangle, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

interface ScanSummary {
  id: number
  name: string
  format: string
  components_count: number
  vulns_total: number
  vulns_critical: number
  vulns_kev: number
  uploaded_by: string
  created_at: string
}

interface Finding {
  component: string
  version: string
  ecosystem: string
  vuln_id: string
  cve: string | null
  summary: string
  severity: string | null
  cvss_score: number | null
  kev: boolean
  ransomware: boolean
  epss_score: number | null
}

export default function SbomPage() {
  const [scans, setScans] = useState<ScanSummary[]>([])
  const [selected, setSelected] = useState<{ summary: ScanSummary; findings: Finding[] } | null>(null)
  const [name, setName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const fetchScans = useCallback(async () => {
    try {
      const r = await api.get('/api/sbom/scans', { params: { limit: 30 } })
      setScans(r.data?.scans || [])
    } catch {}
  }, [])

  useEffect(() => { fetchScans() }, [fetchScans])

  const onUpload = async () => {
    const f = fileRef.current?.files?.[0]
    if (!f) { setError('Pick a CycloneDX or SPDX JSON file first'); return }
    if (!name.trim()) { setError('Give the scan a name'); return }
    setUploading(true); setError(null)
    try {
      const text = await f.text()
      const sbom = JSON.parse(text)
      const r = await api.post(`/api/sbom/scan`, sbom, { params: { name } })
      const sid = r.data?.scan_id
      if (sid) {
        const det = await api.get(`/api/sbom/scans/${sid}`)
        setSelected({ summary: det.data, findings: det.data.findings || [] })
      }
      setName(''); if (fileRef.current) fileRef.current.value = ''
      fetchScans()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const openScan = async (id: number) => {
    try {
      const r = await api.get(`/api/sbom/scans/${id}`)
      setSelected({ summary: r.data, findings: r.data.findings || [] })
    } catch {}
  }

  const deleteScan = async (id: number) => {
    if (!confirm('Delete this scan?')) return
    try { await api.delete(`/api/sbom/scans/${id}`); fetchScans() } catch {}
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Package size={24} className="text-blue" /> SBOM Scanner
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Upload CycloneDX or SPDX. Cross-references every component@version against NVD, CISA KEV, EPSS.
          </p>
        </div>
      </div>

      <div className="card p-4">
        <h2 className="text-sm font-semibold text-text-primary mb-2">New scan</h2>
        <div className="flex flex-wrap items-center gap-2">
          <input value={name} onChange={e => setName(e.target.value)}
            placeholder="checkout-service v2.4.1"
            className="input py-1.5 text-xs w-64" />
          <input type="file" accept=".json,application/json" ref={fileRef}
            className="text-xs" />
          <button type="button" onClick={onUpload} disabled={uploading}
            className="btn-primary text-xs">
            {uploading ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? 'Scanning…' : 'Scan'}
          </button>
          {error && <span className="text-[11px] text-rose">{error}</span>}
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          Drop a CycloneDX 1.4+ JSON or SPDX 2.3 JSON. Scan time ~5-30s depending on component count.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-1 card p-3">
          <h3 className="text-[11px] uppercase tracking-wider text-text-muted font-semibold mb-2">Recent scans</h3>
          <div className="space-y-1 max-h-[600px] overflow-y-auto">
            {scans.length === 0 && <p className="text-[11px] text-text-muted italic">No scans yet.</p>}
            {scans.map(s => (
              <button key={s.id} type="button" onClick={() => openScan(s.id)}
                className={clsx('w-full text-left p-2 rounded border text-xs',
                  selected?.summary?.id === s.id ? 'border-blue bg-blue/10' : 'border-border hover:bg-surface-hover')}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-text-primary truncate">{s.name}</span>
                  <button type="button" onClick={e => { e.stopPropagation(); deleteScan(s.id) }}
                    className="text-text-muted hover:text-rose"><Trash2 size={10} /></button>
                </div>
                <div className="text-[10px] text-text-muted mt-0.5">
                  {s.components_count} components · {s.vulns_total} vulns
                  {s.vulns_kev > 0 && <span className="text-rose-light ml-1">⚠ {s.vulns_kev} KEV</span>}
                </div>
                <div className="text-[9px] text-text-muted">{formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          {!selected && (
            <div className="card p-6 text-center text-text-muted text-sm">
              Pick a scan on the left or upload a new SBOM above.
            </div>
          )}
          {selected && (
            <>
              <div className="card p-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-text-primary">{selected.summary.name}</h2>
                  <span className="text-[10px] text-text-muted">{selected.summary.format}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <Stat label="Components" value={selected.summary.components_count} tone="text" />
                  <Stat label="Vulns" value={selected.summary.vulns_total} tone="amber" />
                  <Stat label="Critical" value={selected.summary.vulns_critical} tone="rose" />
                  <Stat label="KEV" value={selected.summary.vulns_kev} tone="rose" pulse={selected.summary.vulns_kev > 0} />
                </div>
              </div>

              <div className="card p-3">
                <h3 className="text-xs font-semibold text-text-primary mb-2">Findings</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                        <th className="text-left py-1.5 px-2">Component</th>
                        <th className="text-left py-1.5 px-2">CVE</th>
                        <th className="text-right py-1.5 px-2">CVSS</th>
                        <th className="text-right py-1.5 px-2">EPSS</th>
                        <th className="text-left py-1.5 px-2">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.findings.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-4 text-emerald-light text-[11px]">
                          No vulnerabilities found 🎉
                        </td></tr>
                      )}
                      {selected.findings.slice(0, 80).map((f, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-surface-hover">
                          <td className="py-1.5 px-2">
                            <div className="text-text-primary truncate max-w-[180px]">{f.component}</div>
                            <div className="text-[9px] text-text-muted">{f.ecosystem} {f.version}</div>
                          </td>
                          <td className="py-1.5 px-2 font-mono">
                            {f.cve ? (
                              <Link to={`/app/cve/${encodeURIComponent(f.cve)}`} className="text-blue-light hover:underline">{f.cve}</Link>
                            ) : (
                              <span className="text-text-muted">{f.vuln_id}</span>
                            )}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono">{f.cvss_score?.toFixed(1) || '—'}</td>
                          <td className="py-1.5 px-2 text-right font-mono">{f.epss_score != null ? `${(f.epss_score * 100).toFixed(0)}%` : '—'}</td>
                          <td className="py-1.5 px-2 space-x-1">
                            {f.kev && <span className="badge-red text-[9px]">KEV</span>}
                            {f.ransomware && <span className="badge text-[9px] bg-purple-500/20 text-purple-300 border border-purple-500/40">RW</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, tone, pulse }: { label: string; value: number; tone: 'rose' | 'amber' | 'text'; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : 'text-text-primary'
  return (
    <div className="rounded-md bg-surface-raised p-2 text-center">
      <div className="text-[9px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold font-mono mt-0.5', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
